import React, { useState, useRef } from 'react';
import { apiFetch } from '../utils/apiUtils';
import { toast } from 'react-toastify';

const ROUTE_LABELS = {
  'jerusalem-newyork': 'Jerusalem → Milan → New York',
  'jerusalem-milan': 'Jerusalem → Milan',
};

export default function AddPassengerModal({ flightConfigs, accessCode, onClose, onSuccess }) {
  const [step, setStep] = useState('flight'); // flight | form | confirm | processing | done
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const [np, setNp] = useState({
    first_name: '', last_name: '', birth_date: '', gender: 'Male',
    email: '', phone: '', school: '',
    passport_number: '', passport_country: 'United States', passport_expiration: '',
  });
  const [passportFile, setPassportFile] = useState(null);
  const [passportFileName, setPassportFileName] = useState('');
  const [uploadingPassport, setUploadingPassport] = useState(false);
  const fileInputRef = useRef(null);

  const flightConfig = selectedFlight ? flightConfigs?.[selectedFlight] : null;
  const price = flightConfig?.routes?.[selectedRoute]?.price;

  const updateNp = (field, value) => setNp(prev => ({ ...prev, [field]: value }));

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25MB)'); return; }
    setPassportFile(file);
    setPassportFileName(file.name);
  };

  const uploadPassport = async () => {
    if (!passportFile) return null;
    setUploadingPassport(true);
    try {
      const ext = passportFile.name.split('.').pop() || 'pdf';
      const safeName = `${np.last_name || 'unknown'}-${np.first_name || 'unknown'}`.replace(/[^a-zA-Z0-9-]/g, '');
      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-upload-url?fileName=${encodeURIComponent(safeName + '.' + ext)}&contentType=${encodeURIComponent(passportFile.type)}`);
      if (res.status !== 'success') throw new Error(res.message || 'Failed to get upload URL');
      const putRes = await fetch(res.presignedUrl, { method: 'PUT', body: passportFile, headers: { 'Content-Type': passportFile.type } });
      if (!putRes.ok) throw new Error('Upload failed');
      return res.s3Key;
    } catch (err) {
      toast.error('Passport upload failed: ' + err.message);
      return null;
    } finally {
      setUploadingPassport(false);
    }
  };

  const handleSubmit = async () => {
    setStep('processing');
    setError('');
    try {
      let passportS3Key = null;
      if (passportFile) {
        passportS3Key = await uploadPassport();
        if (!passportS3Key) { setStep('form'); return; }
      }

      const res = await apiFetch(`${import.meta.env.VITE_API_URL}/flight-admin/add-passenger`, {
        method: 'POST',
        body: JSON.stringify({
          access_code: accessCode,
          flightId: selectedFlight,
          route: selectedRoute,
          passenger: np,
          passportS3Key,
        }),
      });

      if (res.status === 'success') {
        setResult(res);
        setStep('done');
        toast.success(res.message);
      } else {
        setError(res.message || 'Failed');
        setStep('confirm');
        toast.error(res.message || 'Failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
      setStep('confirm');
      toast.error(err.message);
    }
  };

  const selectFlightRoute = (flightId, route) => {
    setSelectedFlight(flightId);
    setSelectedRoute(route);
    setStep('form');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-8 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 mb-8">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-xl">
          <h2 className="text-lg font-bold text-gray-800">Add New Passenger</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="px-6 py-5">
          {/* Step 1: Select Flight + Route */}
          {step === 'flight' && (
            <div>
              <h3 className="font-bold text-gray-800 mb-3">Select Flight & Route</h3>
              <div className="space-y-3">
                {Object.entries(flightConfigs || {}).map(([fId, fc]) => (
                  <div key={fId} className="border rounded-lg p-4">
                    <p className="font-semibold text-gray-800 mb-2">{fc.label || fId}</p>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(fc.routes || {}).map(([rk, rv]) => (
                        <button key={rk} onClick={() => selectFlightRoute(fId, rk)}
                          className="px-4 py-2 rounded-lg border-2 border-blue-200 hover:border-blue-500 hover:bg-blue-50 text-sm transition-all">
                          <span className="font-medium">{ROUTE_LABELS[rk] || rk}</span>
                          <span className="ml-2 font-bold text-blue-700">${rv.price?.toLocaleString()}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Passenger Form */}
          {step === 'form' && (
            <div>
              <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm">
                <strong>{flightConfig?.label}</strong> — {ROUTE_LABELS[selectedRoute]} — <strong>${price?.toLocaleString()}</strong>
                <button onClick={() => setStep('flight')} className="ml-3 text-blue-600 underline text-xs">Change</button>
              </div>
              <h3 className="font-bold text-gray-800 mb-3">Passenger Details</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
                  <input value={np.first_name} onChange={e => updateNp('first_name', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
                  <input value={np.last_name} onChange={e => updateNp('last_name', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email *</label>
                  <input value={np.email} onChange={e => updateNp('email', e.target.value)} type="email" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input value={np.phone} onChange={e => updateNp('phone', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Birth Date</label>
                  <input value={np.birth_date} onChange={e => updateNp('birth_date', e.target.value)} type="date" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                  <select value={np.gender} onChange={e => updateNp('gender', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Passport Number</label>
                  <input value={np.passport_number} onChange={e => updateNp('passport_number', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Passport Country</label>
                  <input value={np.passport_country} onChange={e => updateNp('passport_country', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Passport Expiration</label>
                  <input value={np.passport_expiration} onChange={e => updateNp('passport_expiration', e.target.value)} type="date" className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">School</label>
                  <input value={np.school} onChange={e => updateNp('school', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-xs font-medium text-gray-600 mb-1">Passport Document</label>
                <div className="flex items-center gap-3">
                  <button onClick={() => fileInputRef.current?.click()} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                    {passportFileName ? 'Change File' : 'Upload Passport'}
                  </button>
                  {passportFileName && <span className="text-sm text-green-700 font-medium">{passportFileName}</span>}
                  <input ref={fileInputRef} type="file" accept="image/*,application/pdf" onChange={handleFileChange} className="hidden" />
                </div>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setStep('flight')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">&larr; Back</button>
                <button onClick={() => setStep('confirm')}
                  disabled={!np.first_name || !np.last_name || !np.email}
                  className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all ${np.first_name && np.last_name && np.email ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                  Review &amp; Confirm &rarr;
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 'confirm' && (
            <div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h3 className="font-bold text-green-800 mb-2">Confirm: Add New Passenger</h3>
                <ul className="text-sm text-green-700 space-y-1 list-disc pl-4">
                  <li><strong>{np.first_name} {np.last_name}</strong> will be registered on <strong>{flightConfig?.label}</strong></li>
                  <li>Route: {ROUTE_LABELS[selectedRoute]}</li>
                  <li>Payment link for <strong>${price?.toLocaleString()}</strong> will be sent to <strong>{np.email}</strong></li>
                  {passportFileName && <li>Passport: {passportFileName} will be uploaded</li>}
                  <li>Office will be notified</li>
                </ul>
              </div>
              {error && <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-4 text-sm text-red-700">{error}</div>}
              <div className="flex gap-3">
                <button onClick={() => setStep('form')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">&larr; Back</button>
                <button onClick={handleSubmit} className="flex-1 py-3 rounded-lg font-bold text-sm bg-green-600 hover:bg-green-700 text-white transition-all">
                  Add Passenger &amp; Send Payment Link
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Processing */}
          {step === 'processing' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{uploadingPassport ? 'Uploading passport...' : 'Creating registration...'}</p>
            </div>
          )}

          {/* Step 5: Done */}
          {step === 'done' && result && (
            <div className="text-center py-4">
              <div className="text-4xl mb-3">&#10003;</div>
              <h3 className="text-lg font-bold text-green-700 mb-2">Passenger Added</h3>
              <p className="text-gray-600 mb-4">{result.message}</p>
              <div className="bg-gray-50 rounded-lg p-3 mb-3 text-sm">
                <span className="text-gray-500">Reg ID:</span> <strong>{result.regId}</strong>
                <span className="text-gray-400 ml-2">({result.regCode})</span>
              </div>
              {result.checkoutUrl && (
                <div className="bg-blue-50 rounded-lg p-3 mb-3 text-sm">
                  <span className="text-blue-600">Payment link:</span>{' '}
                  <a href={result.checkoutUrl} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline break-all text-xs">{result.checkoutUrl}</a>
                </div>
              )}
              <p className="text-sm text-gray-400 mt-2">{result.emailsSent || 0} email(s) sent</p>
              <button onClick={() => { onSuccess?.(); onClose(); }} className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700">Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
